/**
 * Page-load profiler — client side of the page-profiling rollout.
 *
 * Produces ``perf_page_events`` rows that pair with the server-side
 * ``perf_request_events`` already landing via the F1 middleware.
 *
 * Three things this captures (deliberately non-exhaustive — start
 * with what we need for the ``/admin/status`` dashboard, extend per
 * widget when a specific signal is missing):
 *
 *   * **Phase transitions** — shell, primary, secondary, visual,
 *     interactive. ``profiler.phase(name)`` records ``mount_ms`` since
 *     the page mount. Each phase fires at most once per page load.
 *   * **Widget mounts** — ``profiler.widget(id).done()`` records the
 *     time from widget creation to ``done()``. Useful for heavy
 *     widgets (map, virtualized table, big chart).
 *   * **TanStack query lifecycle** — paired hook
 *     ``useQueryProfiler(profiler, name, query)`` records
 *     ``query_pending_ms`` (fire → response) and links to the
 *     server ``request_id``.
 *
 * Flushing — events buffer in memory; flushed on (a) every
 * ``FLUSH_INTERVAL_MS``, (b) page hide / unload via ``sendBeacon``,
 * (c) explicit ``profiler.flush()``. The flusher is best-effort —
 * failure to POST is swallowed silently and never breaks the page.
 *
 * Session id lives in ``sessionStorage`` so all page loads in one tab
 * share it; the dashboard groups them into a "session timeline" view.
 */

const ENDPOINT = "/api/v1/perf/page-events";
const SESSION_STORAGE_KEY = "fe:perf-session-id";
const HEARTBEAT_STORAGE_KEY = "fe:perf-heartbeat";
const FLUSH_INTERVAL_MS = 10_000;
const HEARTBEAT_INTERVAL_MS = 4_000;
// Window for the "did the browser die since the last heartbeat?"
// check. A clean ``pagehide`` clears the marker, so anything still
// present that's recent is suspicious — likely a browser crash or
// OOM kill mid-page.
const CRASH_DETECT_WINDOW_MS = 30_000;
const MAX_BUFFER = 180; // keeps room under MAX_EVENTS_PER_BATCH = 200

/** Canonical metric names accepted by the server. Open set — unknown
 *  values pass through, but stick to these where possible so the
 *  dashboard can drive aggregations off a stable vocabulary. */
export type ProfilerMetric =
  | "mount_ms"
  | "render_ms"
  | "query_pending_ms"
  | "query_parse_ms"
  | "payload_bytes"
  | "lcp"
  | "fcp"
  | "inp"
  | "cls"
  | "ttfb"
  | "nav_duration_ms"
  | "long_task_ms"
  | "query_error";

export type ProfilerPhase =
  | "shell"
  | "primary"
  | "secondary"
  | "visual"
  | "interactive";

type PageEvent = {
  page_load_id: string;
  screen_key: string;
  phase?: string;
  widget_id?: string;
  metric: string;
  value: number;
  request_ids?: string[];
  bottleneck_tag?: string;
  details?: Record<string, unknown>;
};

type Batch = {
  session_id: string;
  events: PageEvent[];
};

let _sessionId: string | null = null;

// Active profiler registry — let the legacy ``perf-reporter`` forward
// Web Vitals into the new ``perf_page_events`` table by attaching them
// to whichever ``PageProfiler`` is currently mounted. The reporter is
// a page-singleton; profilers come and go per route mount.
let _activeProfiler: PageProfiler | null = null;

export function getActiveProfiler(): PageProfiler | null {
  return _activeProfiler;
}

function sessionId(): string {
  if (_sessionId) return _sessionId;
  if (typeof window === "undefined") {
    _sessionId = randomUuid();
    return _sessionId;
  }
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      _sessionId = existing;
      return existing;
    }
  } catch {
    // sessionStorage may be unavailable (Safari private mode); fall through.
  }
  _sessionId = randomUuid();
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, _sessionId);
  } catch {
    // ignore
  }
  return _sessionId;
}

function randomUuid(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — kept deliberately small.
  const rand = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
  return `${rand()}${rand()}-${rand()}-${rand()}-${rand()}-${rand()}${rand()}${rand()}`;
}

export type ProfilerInit = {
  /** Stable id for the page — ``predictive/failure-vs-impact-matrix``,
   *  etc. Stays stable across renames of the user-facing label. */
  screenKey: string;
  /** Optional details attached to every event (scenario id, filter
   *  scope hash, plan id). Don't put PII here. */
  defaults?: Record<string, unknown>;
};

export type WidgetHandle = {
  /** Records ``mount_ms`` (or ``render_ms`` when ``opts.metric`` is
   *  supplied) and detaches the handle. Idempotent — calling
   *  ``done()`` twice is a no-op. */
  done: (opts?: {
    metric?: ProfilerMetric;
    details?: Record<string, unknown>;
  }) => void;
};

type CrashMarker = {
  ts: number; // Date.now() at last heartbeat
  screen_key: string;
  page_load_id: string;
};

function readHeartbeat(): CrashMarker | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(HEARTBEAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CrashMarker;
    if (
      typeof parsed.ts !== "number" ||
      typeof parsed.screen_key !== "string" ||
      typeof parsed.page_load_id !== "string"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeHeartbeat(marker: CrashMarker): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HEARTBEAT_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // ignore
  }
}

function clearHeartbeat(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(HEARTBEAT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export class PageProfiler {
  readonly pageLoadId: string;
  readonly screenKey: string;
  private readonly defaults?: Record<string, unknown>;
  private readonly mountTime: number;
  private readonly emittedPhases = new Set<string>();
  private buffer: PageEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pagehideListener: (() => void) | null = null;
  private longTaskObserver: PerformanceObserver | null = null;
  private disposed = false;

  constructor(init: ProfilerInit) {
    this.pageLoadId = randomUuid();
    this.screenKey = init.screenKey;
    this.defaults = init.defaults;
    this.mountTime = nowMs();

    if (typeof window !== "undefined") {
      this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
      // F7 — Crash detection. On mount, look at the previous heartbeat
      // marker; if it's recent (< 30 s) and not cleared by a graceful
      // pagehide, the analyst's last page was killed mid-flight.
      this.detectPriorCrash();
      this.startHeartbeat();
      this.installPagehideListener();
      // Sprint1 A1 — register as the active profiler so the legacy
      // perf-reporter can forward Web Vitals into ``perf_page_events``
      // with our ``page_load_id`` + ``screen_key``.
      //
      // Retire the outgoing one first (2026-09-03). ``_activeProfiler`` is a
      // module singleton, but nothing used to retire the instance it replaced:
      // that instance kept its flush timer, its heartbeat and its Long Tasks
      // observer running for the life of the tab, emitting ``long_task_ms``
      // under a ``page_load_id`` whose screen had already moved on — and never
      // a ``shell`` or ``primary`` phase, because the component that would
      // report them was gone.
      //
      // Measured: **115 of 212 page_load_ids were that**, not page loads.
      // Median lifespan 1,459 s (24 minutes), max 310,852 s (3.6 days). Only
      // 97 ever emitted ``shell`` and only 41 reached ``primary``, so any
      // per-load average over 212 is inflated ~2.2x — and the admin panel
      // derives its figures from exactly that table.
      //
      // Disposing the predecessor also ships its buffer, so nothing it had
      // already measured is lost.
      if (_activeProfiler != null && _activeProfiler !== this) {
        _activeProfiler.dispose();
      }
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      _activeProfiler = this;
      // Sprint2 A2 — Long Tasks observer. Anything > 50 ms blocking on
      // the main thread shows up here (Leaflet mount, Recharts redraw,
      // virtualizer mid-scroll). Tagged with the current screen so the
      // dashboard can attribute jank without hand-correlation.
      this.installLongTaskObserver();
    }
  }

  private installLongTaskObserver(): void {
    if (typeof PerformanceObserver === "undefined") return;
    // ``longtask`` isn't on every browser (Safari ships it ~16.4+).
    // Feature-detect via the supportedEntryTypes list rather than a
    // try/observe roundtrip — keeps construction cheap.
    const supported = (
      PerformanceObserver as unknown as { supportedEntryTypes?: string[] }
    ).supportedEntryTypes;
    if (supported && !supported.includes("longtask")) return;
    try {
      const obs = new PerformanceObserver((list) => {
        if (this.disposed) return;
        for (const entry of list.getEntries()) {
          const duration = entry.duration;
          // The spec floor is 50 ms — but we keep the check so a UA
          // that lowers the threshold doesn't bury the dashboard.
          if (duration < 50) continue;
          this.record({
            metric: "long_task_ms",
            value: duration,
            details: {
              name: entry.name,
              start_time_ms: Math.round(entry.startTime),
            },
          });
        }
      });
      obs.observe({ type: "longtask", buffered: true });
      this.longTaskObserver = obs;
    } catch {
      /* perf instrumentation must never break the page */
    }
  }

  private detectPriorCrash(): void {
    const prior = readHeartbeat();
    if (prior == null) return;
    const elapsed = Date.now() - prior.ts;
    // Always clear so we don't double-report on the next mount.
    clearHeartbeat();
    if (elapsed > CRASH_DETECT_WINDOW_MS) return;
    // Emit on the *prior* screen + page_load — the dashboard pivots
    // on screen_key, so the crash event needs to identify what was
    // open when the browser died, not what's mounting now.
    this.buffer.push({
      page_load_id: prior.page_load_id,
      screen_key: prior.screen_key,
      metric: "crash_suspect",
      value: elapsed,
      details: {
        elapsed_since_last_heartbeat_ms: elapsed,
        detected_on_screen: this.screenKey,
        detected_on_page_load_id: this.pageLoadId,
      },
    });
  }

  private startHeartbeat(): void {
    const tick = () => {
      writeHeartbeat({
        ts: Date.now(),
        screen_key: this.screenKey,
        page_load_id: this.pageLoadId,
      });
    };
    tick(); // immediate so a crash in the first 4 s still leaves a trail
    this.heartbeatTimer = setInterval(tick, HEARTBEAT_INTERVAL_MS);
  }

  private installPagehideListener(): void {
    const handler = () => {
      clearHeartbeat();
      // Ship what is buffered (2026-09-03). Without this, LCP, INP and CLS
      // were collected and then thrown away on every page in the product:
      // perf-reporter emits all three from its OWN ``pagehide`` handlers into
      // ``getActiveProfiler()?.record()``, which only appends to this buffer,
      // and the only ``sendBeacon`` path was ``dispose()`` — a React effect
      // cleanup that does not run on a document unload. Result: **0 LCP, 0 INP
      // and 0 CLS rows in 93,497**, on every screen, ever. FCP and TTFB
      // survived only because they are emitted mid-life and catch the 10 s
      // flush timer.
      //
      // Ordering is load-bearing and it holds by construction: listeners on
      // the same target fire in registration order, ``PerfReporter`` starts
      // from ``app/layout.tsx`` at app boot, and a profiler is constructed per
      // screen mount — so the vitals are already in this buffer by the time
      // this runs. If a profiler is ever constructed before the reporter
      // starts, this flush would race ahead of them.
      this.beaconPendingEvents();
    };
    this.pagehideListener = handler;
    // ``pagehide`` fires on real unloads + bfcache transitions; both
    // are graceful exits the crash detector should NOT flag.
    window.addEventListener("pagehide", handler);
  }

  /** Ship the buffer with ``sendBeacon`` — the only transport that survives a
   *  document unload. Shared by ``pagehide`` and ``dispose`` so there is one
   *  unload path, not two that can drift. */
  private beaconPendingEvents(): void {
    if (this.buffer.length === 0) return;
    if (typeof navigator === "undefined") return;
    const body: Batch = { session_id: sessionId(), events: this.buffer };
    this.buffer = [];
    sendBeaconBatch(body);
  }

  /** Record a phase transition. Each phase fires at most once per
   *  page load — duplicate calls are dropped (the dashboard wants the
   *  first occurrence, not the noisiest one). */
  phase(name: ProfilerPhase, details?: Record<string, unknown>): void {
    if (this.disposed) return;
    if (this.emittedPhases.has(name)) return;
    this.emittedPhases.add(name);
    this.record({
      phase: name,
      metric: "mount_ms",
      value: Math.max(0, nowMs() - this.mountTime),
      details: this.mergeDetails(details),
    });
  }

  /** Open a widget timing handle. Call ``handle.done()`` when the
   *  widget has finished its work (mounted, painted, etc.). */
  widget(id: string): WidgetHandle {
    const t0 = nowMs();
    let done = false;
    return {
      done: (opts) => {
        if (done || this.disposed) return;
        done = true;
        this.record({
          widget_id: id,
          metric: opts?.metric ?? "mount_ms",
          value: Math.max(0, nowMs() - t0),
          details: this.mergeDetails(opts?.details),
        });
      },
    };
  }

  /** Low-level recorder — useful for query / Web Vitals plumbing
   *  that already has a number to ship. */
  record(event: Omit<PageEvent, "page_load_id" | "screen_key">): void {
    if (this.disposed) return;
    this.buffer.push({
      page_load_id: this.pageLoadId,
      screen_key: this.screenKey,
      ...event,
    });
    if (this.buffer.length >= MAX_BUFFER) {
      void this.flush();
    }
  }

  /** Force a flush — called by ``useEffect`` cleanup paths so the
   *  page's last events ship before the route unmounts. */
  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;
    const events = this.buffer;
    this.buffer = [];
    const body: Batch = { session_id: sessionId(), events };
    await postBatch(body);
  }

  /** Stop the periodic flush, ship anything pending. Idempotent. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (_activeProfiler === this) _activeProfiler = null;
    if (this.flushTimer != null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.heartbeatTimer != null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pagehideListener != null && typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.pagehideListener);
      this.pagehideListener = null;
    }
    if (this.longTaskObserver != null) {
      try {
        this.longTaskObserver.disconnect();
      } catch {
        /* ignore */
      }
      this.longTaskObserver = null;
    }
    // A clean unmount is a graceful exit — the next page mount must
    // not see a stale heartbeat and flag this as a crash.
    clearHeartbeat();
    // Last-chance flush via sendBeacon — survives the page unload.
    this.beaconPendingEvents();
  }

  private mergeDetails(
    extra?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    if (!this.defaults && !extra) return undefined;
    return { ...(this.defaults ?? {}), ...(extra ?? {}) };
  }
}

function nowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

async function postBatch(body: Batch): Promise<void> {
  if (typeof fetch === "undefined") return;
  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
      keepalive: true,
    });
  } catch {
    // Swallowed — telemetry never breaks the page.
  }
}

function sendBeaconBatch(body: Batch): void {
  try {
    const blob = new Blob([JSON.stringify(body)], {
      type: "application/json",
    });
    navigator.sendBeacon(ENDPOINT, blob);
  } catch {
    // ignore
  }
}
